import React from 'react';
import { Col } from 'reactstrap';
import styled from 'styled-components';
import { HorizontalDiv, VerticalDiv } from '../../components/Common';
import NavigationBar from '../../components/NavigationBar/NavigationBar';
import TitleBar from '../../components/TitleBar';
import buttonTheme from '../../res/styles/theme_button.module.scss';
import { sendPushNotification } from '../../service/ClientService';
import { toastError, toastInfo, toastSuccess, toastWarning } from '../../ui/ToastHelper';
import Checkbox from './../../components/Checkbox';
import EditableText from './../../components/EditableText';
import colors from './../../res/styles/Colors';
import dimension from './../../res/styles/Responsive';
import { getClients } from './../../service/ClientService';
import { ControlPanelPage, PanelFieldTitle } from './components/Common';

const MAX_CHAR_ALLOWED = 107;

SendPushMessages.whyDidYouRender = true;

export default function SendPushMessages() {
    const [charCounter, setCharCounter] = React.useState(0);
    const [message, setMessage] = React.useState();
    const [clients, setClients] = React.useState([]);
    const [clientCodesSelected, setClientCodesSelected] = React.useState(new Set());

    React.useEffect(() => {
        getClients((clients) => {
            setClients(clients);
        });
        return () => {
            setClientCodesSelected(new Set());
        };
    }, []);

    const onSelectAll = () => {
        if (clientCodesSelected.size === clients.length) {
            setClientCodesSelected(new Set());
        } else {
            setClientCodesSelected(new Set(clients.map((c) => c.code)));
        }
    };

    const onClientSelected = (clientCode) => {
        const newClientCodesSelected = new Set(clientCodesSelected);
        if (newClientCodesSelected.has(clientCode)) {
            newClientCodesSelected.delete(clientCode);
        } else {
            newClientCodesSelected.add(clientCode);
        }
        setClientCodesSelected(newClientCodesSelected);
    };

    const onMessageChange = React.useCallback((_, message) => {
        setMessage(message);
    }, []);

    const onUpdateCharCounter = React.useCallback((value) => {
        setCharCounter(value.length);
    }, []);

    const onSend = () => {
        if (charCounter > MAX_CHAR_ALLOWED) {
            toastWarning('Message too long.');
        } else if (message == null || message.length === 0) {
            toastWarning('Invalid message: fill in the message form.');
        } else if (clientCodesSelected.size === 0) {
            toastWarning('No users selected.');
        } else {
            toastInfo('Sending message…');
            sendPushNotification(
                message,
                Array.from(clientCodesSelected),
                (successfulNotificationCount) => {
                    toastSuccess(`Message sent to ${successfulNotificationCount} devices`);
                },
                () => {
                    toastError('Sending failed');
                }
            );
        }
    };

    const remainingChars = MAX_CHAR_ALLOWED - charCounter;

    return (
        <PageWrapper>
            <NavigationBar />
            <TitleBar title='Send Push Messages' />
            <StyledControlPanelPage>
                <ContentColumn xs={12} md={8} lg={6}>
                    <MessageSection>
                        <PanelFieldTitle>
                            Compose the message to send to techs with attempt at days: 3, 4, 7
                        </PanelFieldTitle>
                        <TextAreaContainer>
                            <StyledEditableText
                                rows={6}
                                initialValue={''}
                                onBlur={onMessageChange}
                                onChange={onUpdateCharCounter}
                            />
                        </TextAreaContainer>
                        <CharCounter>{`${remainingChars} char available`}</CharCounter>
                    </MessageSection>

                    <ClientListSection>
                        <StyledListTitleContainer>
                            <PanelFieldTitle>
                                Select clients related to the message:
                            </PanelFieldTitle>
                            <p className='clickable' onClick={onSelectAll}>
                                select all
                            </p>
                        </StyledListTitleContainer>
                        <ScrollableClientList>
                            {clients.map((client) => (
                                <StyledClientItem
                                    data={client}
                                    isSelected={clientCodesSelected.has(client.code)}
                                    onClientSelected={onClientSelected}
                                    key={client.pk}
                                />
                            ))}
                        </ScrollableClientList>
                    </ClientListSection>

                    <ButtonContainer>
                        <button className={buttonTheme.green} onClick={onSend}>
                            SEND
                        </button>
                    </ButtonContainer>
                </ContentColumn>
            </StyledControlPanelPage>
        </PageWrapper>
    );
}

function ClientItem(props) {
    return (
        <HorizontalDiv className={props.className} style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
            <Checkbox
                style={{ transform: 'translateY(30%)', flexShrink: 0 }}
                isEditable
                isSelected={props.isSelected}
                onChange={() => props.onClientSelected(props.data.code)}
            />
            <VerticalDiv style={{ flex: 1, minWidth: 0 }}>
                <p>
                    <b>Code: </b>
                    {props.data.code}
                </p>
                <p>
                    <b>Name: </b>
                    {props.data.name}
                </p>
            </VerticalDiv>
        </HorizontalDiv>
    );
}

/* Page wrapper - full viewport height, no scroll */
const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100vh;
    width: 100vw;
    max-width: 100vw;
    overflow: hidden;
    box-sizing: border-box;
`;

/* Content column that holds all the sections */
const ContentColumn = styled(Col)`
    display: flex;
    flex-direction: column;
    height: 100%;
    max-width: 100%;
    padding: 0 15px;
    margin: 0 auto;
    overflow: hidden;
    box-sizing: border-box;
    
    @media ${dimension.md} {
        padding: 0 20px;
    }
`;

/* Message section - fixed size, contains textarea and counter */
const MessageSection = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    flex-shrink: 0;
    margin-bottom: 15px;
    box-sizing: border-box;
`;

const TextAreaContainer = styled.div`
    width: 100%;
    max-width: 100%;
    flex-shrink: 0;
    box-sizing: border-box;
`;

const StyledEditableText = styled(EditableText)`
    width: 100%;
    max-width: 100%;
    
    textarea {
        width: 100%;
        max-width: 100%;
        min-height: 100px;
        box-sizing: border-box;
    }
`;

const CharCounter = styled.p`
    font-size: 12px;
    color: ${colors.darker_gray};
    text-align: right;
    margin: 5px 10px 10px 0;
    flex-shrink: 0;
`;

/* Client list section - takes remaining space */
const ClientListSection = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 100%;
    flex: 1;
    min-height: 0;
    margin-bottom: 15px;
    overflow: hidden;
    box-sizing: border-box;
`;

const ScrollableClientList = styled.div`
    width: 100%;
    max-width: 100%;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 5px;
    box-sizing: border-box;

    /* Custom scrollbar for better UX */
    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: ${colors.light_gray};
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: ${colors.darker_gray};
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: ${colors.green};
    }
`;

/* Button container - stays at the bottom with proper spacing */
const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
    max-width: 100%;
    padding: 10px 0 20px 0;
    flex-shrink: 0;
    box-sizing: border-box;

    button {
        width: 150px;
        min-width: 120px;
        max-width: 100%;
    }
`;

const StyledClientItem = styled(ClientItem)`
    width: 100%;
    max-width: 100%;
    padding-bottom: 8px;
    box-sizing: border-box;
    
    label {
        /* checkbox */
        transform: translateY(30%);
    }

    p {
        font-size: 12px;
        /* space from checkbox */
        margin-left: 30px;
        word-wrap: break-word;
        overflow-wrap: break-word;

        @media ${dimension.md} {
            font-size: 13px;
        }
    }
`;

const StyledListTitleContainer = styled(HorizontalDiv)`
    justify-content: space-between;
    flex-shrink: 0;
    margin-bottom: 10px;

    .clickable {
        color: ${colors.red};
        cursor: pointer;
    }
`;

const StyledControlPanelPage = styled(ControlPanelPage)`
    display: flex;
    justify-content: center;
    flex: 1;
    width: 100%;
    max-width: 100vw;
    overflow: hidden;
    box-sizing: border-box;
    padding-top: 10px;

    p {
        margin-bottom: 0;
    }

    textarea {
        resize: none;
    }
`;
